/**
 * Hub chat UI (FAB + Drawer). Gate Active AI: dùng `ToppionAIAssistant` — không mount trực tiếp trên _app.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { buildActionPlanV2AiPageContext } from '@/components/common/ActionPlanV2/actionPlanAiPageContext';
import {
  useActionPlanAiChatConfirm,
  useActionPlanAiChatConfirmAll,
  useActionPlanAiObjectivePick,
  useActionPlanAiTaskListPick,
} from '@/components/common/ActionPlanV2/actionPlanAiChatHandlers';
import { isActionPlanV2AiPath } from '@/components/Layout/FloatingAiAssistant';
import { actionPlanAiSuggestions } from '@/components/common/ActionPlanV2/actionPlanAiSuggestions';
import { Apv2TipsButton } from '@/components/common/ActionPlanV2/ui/TipsButton';
import { useAuthStore } from '@/store/useAuthStore';
import { usePrivateStore } from '@/store/usePrivateStore';
import { Drawer, Dropdown, Tooltip } from 'antd';
import {
  CloseOutlined,
  FormOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import Image from 'next/image';
import { aiAgentApi } from '@/services/knowledge/ai-agent-api';
import type { AiHubBudget, AiHubConversation } from '@/types/ai-agent';
import { getAiPageContext } from './aiPageContext';
import { AiChatView } from './AiChat/AiChatView';
import { useAiChat } from './AiChat/useAiChat';
import { AiAssistantBubble } from './AiHub/AiAssistantBubble';
import { AiStepAssistantDrawer } from './AiStepAssistant/AiStepAssistantDrawer';
import { useAiStepSkill } from './AiStepAssistant/aiStepSkillRegistry';
import { useActiveAiGate } from './aiAssistant/useActiveAiGate';
import panelStyles from './AiAssistantPanel.module.css';
import chatStyles from './AiChat/AiChatView.module.css';

type HubProps = {
  stepSuggestOn?: boolean;
};

/** Panel Hub — giả định parent đã bật gate (ToppionAIAssistant). */
export function AiChatBubbleHub({ stepSuggestOn = false }: HubProps) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [stepOpen, setStepOpen] = useState(false);
  const [budget, setBudget] = useState<AiHubBudget | null>(null);
  const [history, setHistory] = useState<AiHubConversation[]>([]);
  const stepSkill = useAiStepSkill();

  const context = useMemo(() => getAiPageContext(router.pathname), [router.pathname]);
  const profile = useAuthStore((s) => s.profile);
  const profileId = profile?.id ?? '';
  const idSelect = usePrivateStore((s) => s.getRequestHeader().selectTree?.idSelected || '');
  const storeYear = usePrivateStore((s) => s.giaiDoan?.year);
  const apV2Chat = isActionPlanV2AiPath(router.pathname || '/');
  const pageContext = useMemo(() => {
    if (apV2Chat) {
      return buildActionPlanV2AiPageContext(
        router.asPath || '/action-plan-v2',
        idSelect || profileId,
        storeYear ?? new Date().getFullYear(),
      );
    }
    return { route: context.route, step: context.step, label: context.label };
  }, [apV2Chat, context.label, context.route, context.step, idSelect, profileId, router.asPath, storeYear]);
  const chat = useAiChat({ pageContext });
  const handleActionPlanConfirm = useActionPlanAiChatConfirm(chat);
  const handleActionPlanConfirmAll = useActionPlanAiChatConfirmAll(chat, handleActionPlanConfirm);
  const handleActionPlanTaskPick = useActionPlanAiTaskListPick(chat);
  const handleActionPlanObjectivePick = useActionPlanAiObjectivePick(chat);

  useEffect(() => {
    if (!open || chat.streaming) return;
    void aiAgentApi.me().then((res) => res?.success && setBudget(res.data.budget)).catch(() => undefined);
    void chat.refreshLlmCatalog();
  }, [open, chat.streaming, chat.refreshLlmCatalog]);

  const loadHistory = () => {
    void aiAgentApi
      .conversations('global')
      .then((res) => res?.success && setHistory(res.data))
      .catch(() => undefined);
  };

  const skills = useMemo(() => {
    if (!stepSuggestOn || !stepSkill?.adapters.length) return [];
    return [
      {
        key: 'ai-step-suggest',
        label: '✨ AI gợi ý',
        title: 'Đề xuất nội dung cho bước đang mở — xem trước rồi áp dụng',
        onClick: () => setStepOpen(true),
      },
    ];
  }, [stepSuggestOn, stepSkill]);

  const historyItems = history.length
    ? history.map((c) => ({
        key: c.id,
        label: (
          <div className={chatStyles.historyItem}>
            <span className={chatStyles.historyTitle}>{c.title || 'Cuộc trò chuyện'}</span>
            {c.updated_at ? (
              <span className={chatStyles.historyMeta}>
                {new Date(c.updated_at).toLocaleString('vi-VN', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            ) : null}
          </div>
        ),
      }))
    : [{ key: 'none', label: 'Chưa có lịch sử', disabled: true }];

  return (
    <>
      {!open && <AiAssistantBubble title="Trợ lý BSC" onClick={() => setOpen(true)} />}
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        width="50%"
        className={`${panelStyles.drawer} ${chatStyles.chatDrawer}`}
        closable={false}
        title={(
          <div className={chatStyles.drawerHeader}>
            <div className={chatStyles.drawerBrand}>
              <span className={chatStyles.drawerAvatar}>
                <Image src="/icon/agent.png" alt="" width={28} height={28} unoptimized />
              </span>
              <span className={chatStyles.drawerTitle}>Trợ lý BSC</span>
              {apV2Chat ? <span className={chatStyles.drawerContext}>Action Plan V2</span> : null}
            </div>

            <div className={chatStyles.drawerActions} role="toolbar" aria-label="Thao tác chat">
              <div className={chatStyles.actionGroup}>
                {apV2Chat ? (
                  <span className={chatStyles.headerTips}>
                    <Apv2TipsButton variant="assistant" />
                  </span>
                ) : null}
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  onOpenChange={(o) => o && loadHistory()}
                  overlayClassName={chatStyles.historyPopup}
                  menu={{
                    items: historyItems,
                    onClick: ({ key }) => key !== 'none' && void chat.loadConversation(key),
                  }}
                >
                  <Tooltip title="Lịch sử" placement="bottom">
                    <button type="button" className={chatStyles.iconBtn} aria-label="Lịch sử trò chuyện">
                      <HistoryOutlined />
                    </button>
                  </Tooltip>
                </Dropdown>
                <Tooltip title="Cuộc trò chuyện mới" placement="bottom">
                  <button
                    type="button"
                    className={chatStyles.iconBtn}
                    onClick={chat.reset}
                    aria-label="Cuộc trò chuyện mới"
                  >
                    <FormOutlined />
                  </button>
                </Tooltip>
              </div>
              <span className={chatStyles.actionDivider} aria-hidden />
              <Tooltip title="Đóng" placement="bottom">
                <button
                  type="button"
                  className={`${chatStyles.iconBtn} ${chatStyles.iconBtnClose}`}
                  onClick={() => setOpen(false)}
                  aria-label="Đóng trợ lý"
                >
                  <CloseOutlined />
                </button>
              </Tooltip>
            </div>
          </div>
        )}
        destroyOnHidden={false}
        styles={{
          body: { padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
          header: { padding: 0 },
          wrapper: { maxWidth: '100%' },
        }}
      >
        <AiChatView
          turns={chat.turns}
          streaming={chat.streaming}
          lastPrompt={chat.lastPrompt}
          onSend={(m) => void chat.send(m)}
          onStop={chat.stop}
          onApplyOfferChange={chat.updateApplyOffer}
          // Thẻ duyệt có ở mọi trang: Action Plan V2 và dữ liệu B1–B5 (plan bsc_*) trên trang bước
          onActionPlanPendingChange={chat.updateActionPlanPending}
          onActionPlanConfirm={handleActionPlanConfirm}
          onActionPlanConfirmAll={apV2Chat ? handleActionPlanConfirmAll : undefined}
          onActionPlanTaskListChange={apV2Chat ? chat.updateActionPlanTaskList : undefined}
          onActionPlanAssigneeChange={apV2Chat ? chat.updateActionPlanAssigneeList : undefined}
          onActionPlanTaskPick={apV2Chat ? handleActionPlanTaskPick : undefined}
          onActionPlanObjectivePick={apV2Chat ? handleActionPlanObjectivePick : undefined}
          llmConfig={chat.llmConfig}
          onLlmConfigChange={chat.setLlmConfig}
          llmCatalog={chat.llmCatalog}
          skills={skills}
          contextLabel={context.label}
          contextInHeader={apV2Chat}
          pageStep={context.step}
          suggestions={apV2Chat ? actionPlanAiSuggestions(profile) : context.suggestions}
          budget={budget}
        />
      </Drawer>

      {stepOpen && stepSkill?.adapters.length ? (
        <AiStepAssistantDrawer
          open={stepOpen}
          onClose={() => setStepOpen(false)}
          adapters={stepSkill.adapters}
          onApplied={stepSkill.onApplied}
        />
      ) : null}
    </>
  );
}

/** @deprecated Dùng `ToppionAIAssistant` trên _app. Giữ cho import cũ. */
export function AiChatBubble({ enabled = true }: { enabled?: boolean }) {
  const { ready, stepSuggestOn } = useActiveAiGate({ enabled, loadStepSuggest: true });
  if (!ready) return null;
  return <AiChatBubbleHub stepSuggestOn={stepSuggestOn} />;
}

export default AiChatBubble;
